import React, { FunctionComponent } from 'react';
import { pathOr, path } from 'ramda';
import { Grid } from 'semantic-ui-react';

import amexLogo from 'assets/icons/amex.svg';
import mastercardLogo from 'assets/icons/mastercard.png';
import visaLogo from 'assets/icons/visa.png';
import {
  ChartPlaceholder,
  RadialChartPlaceholder,
} from 'components/Placeholder';
import TransactionsChart from 'components/TransactionsChart';
import { Transactions } from 'components/TransactionsChart/TransactionsChart.helpers';
import Query from 'components/Query';

import Header from 'scenes/NetworkHealth/components/Header';
import EventRadialChart from './components/EventRadialChart';

import {
  ratioChartTransformer,
  CARD_EVENT_LEGEND,
  RECEIVED_CARD_EVENTS_LEGEND,
  TransactionEventRatio,
} from './CardEventsSection.helpers';
import GET_CARD_EVENTS from './CardEventsSection.queries';

type QueryResponse = {
  data: {
    cnTransactionEventsByMarketplaceByType: {
      [key: string]: {
        VISA: Transactions;
        MASTERCARD: Transactions;
        AMEX: Transactions;
      };
    };
    cnTransactionEventRatioByMarketplace: {
      [key: string]: {
        VISA: TransactionEventRatio;
        MASTERCARD: TransactionEventRatio;
        AMEX: TransactionEventRatio;
      };
    };
  };
  loading?: boolean;
};

type Props = {
  startDate: string;
  endDate: string;
  marketplace: string;
};

const renderTwoChartRow = () => (
  <Grid.Row>
    <Grid.Column width={11}>
      <ChartPlaceholder />
    </Grid.Column>
    <Grid.Column width={5}>
      <RadialChartPlaceholder />
    </Grid.Column>
  </Grid.Row>
);
const renderPlaceholder = () => (
  <Grid>
    <Grid.Row>
      <Grid.Column width={16}>
        <ChartPlaceholder />
      </Grid.Column>
    </Grid.Row>
    {renderTwoChartRow()}
    {renderTwoChartRow()}
    {renderTwoChartRow()}
  </Grid>
);

const renderHeader = (title: string, logo?: string): JSX.Element => (
  <Header src={logo} content={title} />
);

const renderCardEventsSection = (startDate: string, endDate: string) => (
  response: QueryResponse,
) => {
  const cnTransactionEventRatioByMarketplace = pathOr(
    [],
    ['data', 'cnTransactionEventRatioByMarketplace', 'DOSH'],
    response,
  ) as any;

  const cnTransactionEventsByMarketplaceByType = pathOr(
    [],
    ['data', 'cnTransactionEventsByMarketplaceByType', 'DOSH'],
    response,
  ) as any;

  if (response.loading) {
    return renderPlaceholder();
  }

  const {
    VISA: VisaRatioData = [],
    AMEX: AmexRatioData = [],
    MASTERCARD: MastercardData = [],
  } = cnTransactionEventRatioByMarketplace || {};

  const VisaRatio = ratioChartTransformer(VisaRatioData);
  const MasterCardRatio = ratioChartTransformer(MastercardData);
  const AmexRatio = ratioChartTransformer(AmexRatioData);

  return (
    <Grid>
      <Grid.Row>
        <Grid.Column width={16}>
          <TransactionsChart
            header={renderHeader('Received Events')}
            legend={RECEIVED_CARD_EVENTS_LEGEND}
            transactions={cnTransactionEventsByMarketplaceByType}
            tooltip={false}
            tickData={path(
              ['VISA', 'CLEARED'],
              cnTransactionEventsByMarketplaceByType,
            )}
            parameters={{
              startDate,
              endDate,
            }}
          />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={11}>
          <TransactionsChart
            header={renderHeader('Visa Events', visaLogo)}
            legend={CARD_EVENT_LEGEND}
            transactions={cnTransactionEventsByMarketplaceByType['VISA']}
            parameters={{
              startDate,
              endDate,
              cardNetwork: 'VISA',
            }}
          />
        </Grid.Column>
        <Grid.Column width={5}>
          <EventRadialChart CNTransactionsRatio={VisaRatio} />
        </Grid.Column>
      </Grid.Row>

      <Grid.Row>
        <Grid.Column width={11}>
          <TransactionsChart
            header={renderHeader('Amex Events', amexLogo)}
            legend={CARD_EVENT_LEGEND}
            transactions={cnTransactionEventsByMarketplaceByType['AMEX']}
            parameters={{
              startDate,
              endDate,
              cardNetwork: 'AMEX',
            }}
          />
        </Grid.Column>

        <Grid.Column width={5}>
          <EventRadialChart CNTransactionsRatio={AmexRatio} />
        </Grid.Column>
      </Grid.Row>

      <Grid.Row>
        <Grid.Column width={11}>
          <TransactionsChart
            header={renderHeader('Mastercard Events', mastercardLogo)}
            legend={CARD_EVENT_LEGEND}
            transactions={cnTransactionEventsByMarketplaceByType['MASTERCARD']}
            parameters={{
              startDate,
              endDate,
              cardNetwork: 'MASTERCARD',
            }}
          />
        </Grid.Column>

        <Grid.Column width={5}>
          <EventRadialChart CNTransactionsRatio={MasterCardRatio} />
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

const CardEventsSection: FunctionComponent<Props> = ({
  startDate,
  endDate,
}) => (
  <Query
    enableProgressInfo
    render={renderCardEventsSection(startDate, endDate)}
    query={GET_CARD_EVENTS}
    variables={{
      params: {
        startDate,
        endDate,
        marketplace: 'DOSH',
      },
    }}
  />
);
export default CardEventsSection;
